import { useMemo } from "react";
import dayjs from "dayjs";
import { useEpgData } from "./useEpgData";
import { averageActiveTv } from "../utils/activeTv";

// Yesterday's best programmes on this channel, ranked by average active TV.
//
// Yesterday rather than today: the rest of Overview reports on a completed day,
// and a "best of" built from three hours of a morning would rank whatever
// happened to be on at breakfast.

function yesterdayDate() {
  return dayjs().subtract(1, "day").format("YYYY-MM-DD");
}

// rawVideo is keyed by date in one of two formats depending on the endpoint.
function minutesForDay(rawVideo, date) {
  if (!rawVideo) return null;
  const d = dayjs(date);
  const arr = rawVideo[d.format("DD-MM-YYYY")] ?? rawVideo[d.format("YYYY-MM-DD")];
  return Array.isArray(arr) && arr.length === 1440 ? arr : null;
}

// Four rather than three: it puts the same number of rows in this card as the
// live ranking card beside it, so the two match without either being padded
// out to meet the other.
export function useTopProgramsYesterday(channelId, rawVideo, limit = 4) {
  const date = useMemo(yesterdayDate, []);
  const { epgBlocks, loading } = useEpgData(channelId, date);

  const programs = useMemo(() => {
    const minutes = minutesForDay(rawVideo, date);
    if (!minutes || !epgBlocks?.length) return [];

    return epgBlocks
      .map((block) => {
        let start = block.startMinute;
        let end = block.endMinute;
        // A programme running past midnight ends on a lower minute than it
        // starts; only the part that falls inside this day can be measured.
        if (end <= start) end = 1440;

        if (start < 0 || start >= 1440) return null;

        return {
          id: block.id,
          name: block.programName,
          start,
          end: Math.min(end, 1440),
          value: averageActiveTv(minutes.slice(start, Math.min(end, 1440))),
        };
      })
      .filter((p) => p && p.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  }, [epgBlocks, rawVideo, date, limit]);

  return { programs, date, loading };
}
